// «Novedades de esta versión» on the web (shared/API.md §11.3; port of the Windows
// ReleaseNotesPresenter / ShellPage.ReleaseNotes and the Apple sheet). Three entry points:
//   · after an update — ONE sheet, once per build (`ReleaseNotesSchedule.pendingRange` over the build
//     remembered in this browser: an install-level preference that survives sign-out);
//   · the "new version" banner's «Qué hay de nuevo» (notes newer than the running build);
//   · Configuración › «Novedades de la versión» (the history: since=0, limit 20, empty/failed states).
// A chat domain without the `/core-api/app-update/…` proxy answers 404: nothing is shown, ever.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { data } from '../../../bridge/client';
import { ReleaseNotesSchedule, type AppReleaseNotes } from '../../../core/releaseNotes';
import { t } from '../../../i18n/i18n';
import { Button, Dialog, InlineStatus } from '../../kit/Kit';
import { PwaIcon } from './pwaIcons';
import './pwa.css';

const LAST_BUILD_KEY = 'satori.releaseNotes.lastBuild';

/** The running build on the release train (vite.config.ts `__APP_BUILD__`, the Android/Mac versionCode). */
export const RUNNING_BUILD: number = typeof __APP_BUILD__ === 'number' ? __APP_BUILD__ : 0;

function lastShownBuild(): number | null {
  try {
    const v = localStorage.getItem(LAST_BUILD_KEY);
    return v && /^\d+$/.test(v) ? Number(v) : null;
  } catch {
    return null;
  }
}

function rememberBuild(build: number): void {
  try {
    localStorage.setItem(LAST_BUILD_KEY, String(build));
  } catch {
    /* private window: the sheet may come back next launch — harmless */
  }
}

export function ReleaseNotesList({ releases }: { releases: AppReleaseNotes[] }) {
  const several = ReleaseNotesSchedule.showsVersionSubheading(releases);
  return (
    <div className="rn-list">
      {releases.map((r) => {
        const caption = ReleaseNotesSchedule.caption(r, several);
        return (
          <section key={r.versionCode} className="rn-release">
            <h3 className="rn-heading">{ReleaseNotesSchedule.heading(r)}</h3>
            {caption ? <div className="rn-caption">{caption}</div> : null}
            <ul className="rn-items">
              {r.items.map((item, i) => (
                <li key={i} className="rn-item">
                  <span className={`rn-tag rn-tag-${item.kind}`}>{ReleaseNotesSchedule.kindLabel(item.kind)}</span>
                  <span className="rn-text">{item.text}</span>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

// Both dialogs are portalled to <body>: they open from the sidebar's notice slot and the settings
// column, each its own stacking context (the profile photo and the glass bars drew over the scrim).
export function ReleaseNotesDialog({ title, releases, onClose }: { title: string; releases: AppReleaseNotes[]; onClose(): void }) {
  return createPortal(
    <Dialog
      title={title}
      onClose={onClose}
      width={520}
      footer={
        <Button kind="primary" onClick={onClose}>
          {t('releaseNotes.gotIt')}
        </Button>
      }
    >
      <ReleaseNotesList releases={releases} />
    </Dialog>,
    document.body,
  );
}

/** The once-per-build sheet after the workspace is up. `blocked` = another modal is on screen: wait. */
export function AfterUpdateReleaseNotes({ blocked }: { blocked: boolean }) {
  const [releases, setReleases] = useState<AppReleaseNotes[] | null>(null);
  useEffect(() => {
    const range = ReleaseNotesSchedule.pendingRange(lastShownBuild(), RUNNING_BUILD);
    if (!range) return;
    let alive = true;
    // A moment after the workspace paints: never competes with the first frame.
    const timer = setTimeout(() => {
      void data.call('releaseNotes', range.since, range.until, ReleaseNotesSchedule.historyLimit).then(
        (r) => {
          if (!alive || r.status === 'failed') return; // a failure stores nothing: asked again next launch
          // Any answer — empty, or "no proxy here" — stores the running build.
          rememberBuild(RUNNING_BUILD);
          if (r.status === 'ok' && r.releases.length) setReleases(r.releases);
        },
        () => undefined,
      );
    }, 1500);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, []);
  if (!releases || blocked) return null;
  return <ReleaseNotesDialog title={t('releaseNotes.afterUpdateTitle')} releases={releases} onClose={() => setReleases(null)} />;
}

/** Configuración's history (since=0): loading, list, empty, failed with retry. */
export function ReleaseNotesHistoryDialog({ onClose }: { onClose(): void }) {
  const [state, setState] = useState<{ kind: 'loading' } | { kind: 'loaded'; releases: AppReleaseNotes[] } | { kind: 'empty' } | { kind: 'failed' }>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let alive = true;
    setState({ kind: 'loading' });
    void data.call('releaseNotes', 0, null, ReleaseNotesSchedule.historyLimit).then(
      (r) => {
        if (!alive) return;
        if (r.status === 'ok') setState(r.releases.length ? { kind: 'loaded', releases: r.releases } : { kind: 'empty' });
        else if (r.status === 'unavailable') setState({ kind: 'empty' });
        else setState({ kind: 'failed' });
      },
      () => alive && setState({ kind: 'failed' }),
    );
    return () => {
      alive = false;
    };
  }, [attempt]);
  return createPortal(
    <Dialog title={t('releaseNotes.historyTitle')} onClose={onClose} width={560}>
      {state.kind === 'loading' ? <InlineStatus kind="loading" text={t('web.destinations.loading')} /> : null}
      {state.kind === 'failed' ? <InlineStatus kind="error" text={t('releaseNotes.failed')} onRetry={() => setAttempt((n) => n + 1)} /> : null}
      {state.kind === 'empty' ? <p className="rn-empty">{t('releaseNotes.empty')}</p> : null}
      {state.kind === 'loaded' ? <ReleaseNotesList releases={state.releases} /> : null}
    </Dialog>,
    document.body,
  );
}

/** The Configuración entry (beside the version line). */
export function ReleaseNotesSettingsEntry() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="group-rows st-nav-end">
        <button type="button" className="list-row clickable st-nav-row" onClick={() => setOpen(true)} data-testid="release-notes-entry">
          <span className="st-nav-icon st-nav-icon-releaseNotes">
            <PwaIcon name="sparkles" size={16} strokeWidth={1.9} />
          </span>
          <span className="list-row-text">
            <span className="list-row-title">{t('web.releaseNotes.settingsRow')}</span>
            <span className="list-row-subtitle">{t('web.releaseNotes.settingsFooter')}</span>
          </span>
        </button>
      </div>
      {open ? <ReleaseNotesHistoryDialog onClose={() => setOpen(false)} /> : null}
    </>
  );
}
